/**
 * DynaMite — the AI Phone Receptionist, in text (DWEB-121).
 *
 * Until v6.4.8 DynaMite was a 270M-parameter Gemma model running in the
 * visitor's browser. It is now the SAME hosted ElevenLabs agent product as
 * the phone line, on a second surface: a separate agent (its own prompt and
 * greeting — "Thanks for calling" is false in a browser), reading the same
 * three knowledge-base documents by id. The agent's prompt is generated from
 * agent-kb/templates and checked against the live agent by check:agent-kb.
 *
 * KEEP THIS MODULE FREE OF IMPORTS. It is read by src/ (the chat surface, the
 * copy) and by the e2e suite and scripts under plain node, the same way
 * heroContent.js is.
 *
 * The id below MUST equal the "web" agent's id in agent-kb/documents.json —
 * dynamite.spec.js asserts it, so the chat cannot point at the phone agent
 * or at an agent the knowledge-base check does not cover.
 */
export const DYNAMITE_AGENT_ID = 'agent_4101m1qfx434fgwvxdsfh9j7cees';

/**
 * WHERE THE CHAT CODE COMES FROM, so the privacy posture can name it.
 *
 * v6.5.0: the chat surface is the site's own DOM (DynaMiteWidget.js) driving
 * the ElevenLabs JavaScript SDK. Until v6.4.9 it was the drop-in widget
 * (@elevenlabs/convai-widget-embed), which rendered the whole surface inside
 * a shadow root the site could not reach into — nowhere to put the avatar
 * canvas DWEB-131 restores. The SDK is a third-party runtime on the flagship
 * feature, the same class as the MediaPipe wasm that DWEB-77 found loading
 * from jsdelivr at @latest, so it is NOT loaded from a CDN: @elevenlabs/client
 * is an npm dependency pinned to an exact version in package.json, bundled by
 * webpack into a lazy chunk and served from this origin like any other. What
 * it then talks to at runtime is named here so the privacy policy and the
 * suite's network policy can say the same thing.
 */
export const DYNAMITE_SDK_PACKAGE = '@elevenlabs/client';

/**
 * The hosts a session reaches — MEASURED, not inferred from the package
 * name (v6.5.0). The SDK opens one WebSocket to
 * wss://api.elevenlabs.io/v1/convai/conversation (WebSocketConnection.js in
 * the pinned dist); the widget passes `connectionType: 'websocket'`
 * explicitly, so a VOICE session (DWEB-130, v6.6.3) travels the same socket
 * to the same host — the factory never selects WebRTC, and
 * livekit.rtc.elevenlabs.io, the transport the embed also named, is not
 * contacted for either. The dist's only other hosts are the EU/IN residency
 * variants, reachable only through an `origin` option this site does not
 * pass. Since v6.6.3 a session also makes ONE HTTP GET to this host first —
 * DYNAMITE_CAPABILITY_URL below — when the visitor presses Start. The suite
 * records every request and WebSocket the page opens and dynamite.spec.js
 * asserts the set of hosts is exactly this list. Nothing is contacted until
 * the visitor starts a conversation: the embed's page-load configuration
 * fetch is gone with it.
 *
 * One voice-path exception, REPORTED and not taken: in a browser whose
 * getUserMedia lacks the `sampleRate` constraint (Firefox), the SDK's
 * input/output controllers fetch a resampler worklet from
 * cdn.jsdelivr.net (`addLibsamplerateModule.js` in the dist; the SDK's
 * `libsampleratePath` option would self-host it). Chromium and WebKit have
 * the constraint and fetch nothing. The gate's default-deny would name the
 * host if a Chromium path ever took it.
 */
export const DYNAMITE_RUNTIME_HOSTS = ['api.elevenlabs.io'];

/**
 * WHERE THE SITE READS THE AGENT'S CAPABILITY AT SESSION START (DWEB-130,
 * v6.6.3). The public widget-config endpoint — no key; the drop-in widget
 * fetched it on every page load until v6.4.9 — carries
 * `widget_config.text_only`, and the mic control is rendered only when it
 * reads `false`. Established from the SDK and the live agent before
 * building: the handshake's `conversation_initiation_metadata` carries no
 * such flag (conversation_id and the two audio formats only, dist and
 * @elevenlabs/types), and the web agent ALLOWS the client's `text_only`
 * override (platform_settings.overrides…conversation.text_only: true, read
 * 2026-09-05), so a voice attempt against a text-only agent is not refused
 * and cannot be the signal. The site fetches this ONCE, when the visitor
 * presses Start — never on page load — and anything but an explicit `false`
 * (a refusal, a timeout, a missing field) reads as text-only. That is the
 * deploy-order safety: the site can ship voice UI while the agent is still
 * `text_only: true`, and nothing appears until the owner flips it.
 */
export const DYNAMITE_CAPABILITY_URL = `https://${DYNAMITE_RUNTIME_HOSTS[0]}/v1/convai/agents/${DYNAMITE_AGENT_ID}/widget`;

/**
 * The one sentence about what DynaMite IS, for every place the site describes
 * it. One string, several readers (the hero callout, the DynaMite card, the
 * FAQ), so the description cannot drift between them. The knowledge base and
 * index.html's FAQ JSON-LD quote it too and are held to it by the suite.
 */
export const DYNAMITE_DESCRIPTION =
  'DynaMite is the AI Phone Receptionist offer running in text: the same hosted agent product ' +
  'that answers the DynamWorks phone line, answering from the same knowledge base. It quotes the ' +
  'published offers and prices, books nothing, sees no records, and gives no advice. Conversations ' +
  'are processed on the hosted platform, not in your browser — the privacy policy has the details.';

/**
 * WHO PROCESSES A CONVERSATION, by name — for the privacy policy (DWEB-143).
 *
 * The policy is a .docx the site fetches and converts at runtime
 * (PrivacyPolicy.js), so no grep over src/ or the served bundle can read it;
 * e2e/privacy-policy.spec.js converts it and holds it to THIS module: the
 * provider the policy names must be the one the SDK package and the runtime
 * host below belong to, and the retention period it states must be the one
 * the web agent is configured with. Change the agent's `privacy.retention_days`
 * and this number together, or the gate says so.
 */
export const DYNAMITE_PROVIDER = 'ElevenLabs';

/**
 * The web agent's `privacy.retention_days`, read from the live agent on
 * 2026-09-05 (DWEB-143). Since v6.6.3 (DWEB-130) check:agent-kb reads it
 * too — `platform_settings.privacy.retention_days` in documents.json's web
 * surfaceConfig must equal this — so the policy spec holds the published
 * number to this constant and the check holds this constant to the agent.
 * Voice audio (`record_voice`) is retained for the same period, like the
 * telephone line; the policy's assistant paragraph says so.
 */
export const DYNAMITE_RETENTION_DAYS = 30;

/**
 * THE LAUNCHER'S DEFAULT FACE (DWEB-144, v6.6.0; DWEB-146, v6.6.1).
 *
 *   'orb'    — DynamiteOrb.js: G2, lit teal glass, CSS only. Still at rest —
 *              no keyframe, no rAF, no will-change, no canvas; its core is
 *              pulled toward the pointer by a transition and travels into
 *              the panel's header on open. Nothing from three.js is fetched
 *              for it.
 *   'avatar' — DynamiteAvatar.js: the three.js canvas restored in v6.5.1
 *              (DWEB-131) — the procedural robot head.
 *
 * DEFAULT, not only: the interactive demo's "Sync with DynaMite Chatbot"
 * toggle overrides it at runtime, per page load, through AvatarSyncManager
 * — sync on puts the chosen Regina / Roger head in the launcher with the
 * badge "DynaMite (regina)", sync off returns this face (DynaMiteWidget.js
 * subscribes and swaps). The panel's header keeps the orb's core as its
 * state indicator whichever face launched it. That is the owner's ruling on
 * DWEB-146; nothing about the selection is stored.
 *
 * The owner's words: "dont remove the avatar just disconnect so that we can
 * check." So BOTH render paths ship, both are imported, and both are tested
 * on EVERY gate run: the e2e suite has one project per launcher
 * (playwright.config.js), and the second project forces the launcher that
 * is not the default through the runtime override below. A switch whose
 * other setting is never exercised is a deletion with a delay — that is how
 * the avatar was lost at 11113b9 and cost v6.5.1 to restore.
 *
 * RUNTIME OVERRIDE, for the suite and for checking the other launcher on a
 * built site without a redeploy: `?launcher=avatar` in the URL, or
 * `window.__dynamiteLauncher = 'avatar'` set before the bundle runs. Read
 * once at mount (src/utils/dynamiteLauncher.js); any other value falls back
 * to the constant. It selects between two shipped render paths and nothing
 * else — no storage, nothing persisted (DWEB-143's enumeration holds).
 */
export const DYNAMITE_LAUNCHER = 'orb';
export const DYNAMITE_LAUNCHERS = ['orb', 'avatar'];

/**
 * The orb's state model (DWEB-144), carried by the HEADER core since v6.6.1
 * (DWEB-146, DynamiteCore in DynamiteOrb.js): the orb's core, arrived in the
 * panel's header, is the state indicator whichever face launched the panel.
 * 'idle' is what a launcher nobody is talking to is, and in which nothing
 * animates — DWEB-83's criterion is zero rAF callers five seconds after
 * load, and a page nobody is talking to is idle. The other three are driven
 * from the TEXT turn since v6.6.2 (DWEB-147, DynaMiteWidget.js): listening
 * while the composer has focus and the turn is ready, thinking from send to
 * the first streamed part, speaking between the reply's `start` and `stop`
 * — each a `data-state` the core's stylesheet keys motion on (listening
 * breathes the halo, thinking drifts the point, speaking pulses the pin),
 * running only while the state holds, stopping when it returns to idle, and
 * never under prefers-reduced-motion. Since v6.6.3 (DWEB-130) VOICE drives
 * the same four: the SDK's `onModeChange` (speaking while the agent's audio
 * plays, listening when it finishes), `onInterruption` (back to listening)
 * and the visitor's spoken turn (thinking until the reply) — set as state,
 * never polled. SPEAKING IS A STATE, NOT AN ANIMATION — see DynamiteOrb.js:
 * the motion belongs to the state and ends with it; a loop that outlives
 * the turn is the DWEB-83 defect in a new coat, and an audio-level
 * visualiser on requestAnimationFrame would be exactly that.
 */
export const DYNAMITE_ORB_STATES = ['idle', 'listening', 'thinking', 'speaking'];
